import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../../components/snoop/SummaryDashboard'
import MappingAddForm from '../../components/snoop/MappingAddForm'
import {
  createProcessRow,
  deleteProcessRow,
  fetchProcess,
  updateProcessRow,
} from '../../api/operationsClient'
import { PLACEHOLDER_OPS_PROCESS } from '../../utils/snoop/placeholderColumns'

const STATUS_OPTIONS = [
  { value: 'Brainstorm', label: 'Brainstorm' },
  { value: 'Planned', label: 'Planned' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Live', label: 'Live' },
  { value: 'Parked', label: 'Parked' },
]

const AUTOMATION_OPTIONS = [
  { value: 'No', label: 'No' },
  { value: 'Partial', label: 'Partial' },
  { value: 'Yes', label: 'Yes' },
]

const STATUS_FILTER_OPTIONS = [{ value: '', label: 'All statuses' }, ...STATUS_OPTIONS]

/** Board order, so Status sorts by stage rather than alphabetically. */
const STATUS_RANK = {
  Brainstorm: 0,
  Planned: 1,
  'In Progress': 2,
  Live: 3,
  Parked: 4,
}

function compareProcessStatus(a, b) {
  const left = STATUS_RANK[String(a ?? '').trim()]
  const right = STATUS_RANK[String(b ?? '').trim()]
  const l = left === undefined ? 99 : left
  const r = right === undefined ? 99 : right
  if (l !== r) return l - r
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base' })
}

const btnClass =
  'inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

const selectClass =
  'rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 shadow-sm focus:border-gray-400 focus:outline-none'

export default function OpsProcessPage() {
  const [status, setStatus] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const fetchData = useCallback(
    () => fetchProcess({ status: status || undefined }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, refreshKey],
  )

  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'process_mapping',
        uidColumn: 'id',
        rowIdColumn: 'id',
        options: STATUS_OPTIONS,
        allowBlank: false,
        centerText: true,
        timestampColumn: 'Updated At',
        onSave: async ({ status: next, mappingId, uid }) => {
          const id = mappingId ?? uid
          if (id == null || String(id).trim() === '') throw new Error('Missing row id')
          await updateProcessRow(id, { status: next })
        },
      },
      Automation: {
        target: 'process_mapping',
        uidColumn: 'id',
        rowIdColumn: 'id',
        options: AUTOMATION_OPTIONS,
        allowBlank: false,
        centerText: true,
        timestampColumn: 'Updated At',
        onSave: async ({ status: next, mappingId, uid }) => {
          const id = mappingId ?? uid
          if (id == null || String(id).trim() === '') throw new Error('Missing row id')
          await updateProcessRow(id, { automation: next })
        },
      },
    }),
    [],
  )

  const onDeleteRow = useCallback(async (row) => {
    const id = row?.id
    if (id == null || String(id).trim() === '') throw new Error('Missing row id')
    await deleteProcessRow(id)
  }, [])

  const defaultSort = useMemo(
    () => [
      { column: 'Sort Order', direction: 'asc' },
      { column: 'Project', direction: 'asc' },
    ],
    [],
  )

  const columnWeights = useMemo(
    () => ({
      Project: 20,
      Status: 12,
      Scope: 10,
      Type: 9,
      Frequency: 8,
      Automation: 10,
      Category: 9,
      Lead: 8,
      'Sub-Lead': 8,
      Notes: 22,
    }),
    [],
  )

  const addForm = showAdd ? (
    <MappingAddForm
      title="Add process / project"
      submitLabel="Add"
      fields={[
        { name: 'project', label: 'Project', required: true },
        {
          name: 'status',
          label: 'Status',
          type: 'select',
          options: STATUS_OPTIONS,
          defaultValue: 'Brainstorm',
        },
        { name: 'scope', label: 'Scope' },
        { name: 'process_type', label: 'Type' },
        { name: 'frequency', label: 'Frequency' },
        {
          name: 'automation',
          label: 'Automation',
          type: 'select',
          options: AUTOMATION_OPTIONS,
          defaultValue: 'No',
        },
        { name: 'category', label: 'Category' },
        { name: 'lead', label: 'Lead' },
        { name: 'sub_lead', label: 'Sub-Lead' },
        { name: 'notes', label: 'Notes' },
        { name: 'sort_order', label: 'Sort Order' },
      ]}
      onSubmit={async (values) => {
        await createProcessRow(values)
        setRefreshKey((k) => k + 1)
      }}
    />
  ) : null

  const extraActions = (
    <>
      <label className="inline-flex items-center gap-2 text-sm text-gray-600">
        Status
        <select
          className={selectClass}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className={btnClass}
        onClick={() => setShowAdd((on) => !on)}
        aria-pressed={showAdd}
      >
        {showAdd ? 'Close add' : 'Add project'}
      </button>
    </>
  )

  return (
    <SummaryDashboard
      title="Process"
      subtitle="Ops project board — status, owners and automation state"
      fetchData={fetchData}
      defaultSort={defaultSort}
      statusCompare={compareProcessStatus}
      searchPlaceholder="Search project, scope, category, lead…"
      editableStatus={editableStatus}
      onDeleteRow={onDeleteRow}
      extraActions={extraActions}
      addForm={addForm}
      layout="mapping"
      columnWeights={columnWeights}
      wrapColumns={['Notes']}
      truncateColumns={['Scope', 'Category']}
      hiddenColumns={['id', 'Created At']}
      placeholderColumns={PLACEHOLDER_OPS_PROCESS}
      tableKey={`ops-process-${status || 'all'}-${refreshKey}`}
    />
  )
}
